import React, { useEffect, useMemo, useState } from 'react'
import {
    LoaderBar,
    PageHeader,
    AppTable,
    FlexRowBetween,
    AppEditButton,
    AppRemoveButtonPopup,
    FlexBox,
} from 'components'
import { DesktopMode, MobileMode } from 'layout'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { Modal, Form } from 'semantic-ui-react'
import { useAssetLocation } from 'hooks/asset-manager'
import { useAuth } from 'context/user'
import { X, CaretLeft } from 'phosphor-react'
import { useNavigate } from 'react-router-dom'

export const AssetLocationListScreen = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const {
        locations,
        loading,
        fetchLocations,
        getLocation,
        saveLocation,
        deleteLocation,
    } = useAssetLocation()

    const [openModal, setOpenModal] = useState(false)
    const [saving, setSaving] = useState(false)
    const [selectedId, setSelectedId] = useState(0)
    const [locationForm, setLocationForm] = useState({
        LocationName: '',
        LocationCode: '',
        Address1: '',
        Address2: '',
        City: '',
        State: '',
        Zip: '',
        IsActive: true,
    })

    useEffect(() => {
        if (user?.CompanyId) {
            fetchLocations()
        }
    }, [user?.CompanyId, fetchLocations])

    const locationsForUi = Array.isArray(locations) ? locations : []

    const resetForm = () => {
        setSelectedId(0)
        setLocationForm({
            LocationName: '',
            LocationCode: '',
            Address1: '',
            Address2: '',
            City: '',
            State: '',
            Zip: '',
            IsActive: true,
        })
    }

    const handleAdd = () => {
        resetForm()
        setOpenModal(true)
    }

    const handleEdit = async item => {
        const id = item.AssetLocationId || item.id
        if (!id) return
        const details = await getLocation(id)
        const payload = details || item
        setSelectedId(id)
        setLocationForm({
            LocationName: payload.LocationName || '',
            LocationCode: payload.LocationCode || '',
            Address1: payload.Address1 || '',
            Address2: payload.Address2 || '',
            City: payload.City || '',
            State: payload.State || '',
            Zip: payload.Zip || '',
            IsActive: payload.IsActive !== false,
        })
        setOpenModal(true)
    }

    const handleDelete = async item => {
        const id = item.AssetLocationId || item.id
        if (!id) return
        const ok = await deleteLocation(id)
        if (ok) {
            await fetchLocations()
        }
    }

    const handleSave = async () => {
        if (!locationForm.LocationName.trim()) return
        setSaving(true)
        try {
            const result = await saveLocation({
                AssetLocationId: selectedId || 0,
                ...locationForm,
            })
            const isSaveSuccess =
                result !== null && result !== undefined && result !== false
            if (isSaveSuccess) {
                setOpenModal(false)
                resetForm()
                await fetchLocations()
            }
        } finally {
            setSaving(false)
        }
    }

    const columns = useMemo(
        () => [
            { key: 'LocationName', title: 'Location', textAlign: 'left' },
            {
                key: 'LocationCode',
                title: 'Code',
                textAlign: 'left',
                render: item => item.LocationCode || '-',
            },
            {
                key: 'City',
                title: 'City',
                textAlign: 'left',
                render: item => item.City || '-',
            },
            {
                key: 'State',
                title: 'State',
                textAlign: 'left',
                render: item => item.State || '-',
            },
            {
                key: 'actions',
                title: 'Actions',
                textAlign: 'center',
                render: item => (
                    <FlexBox className="justify-content-center">
                        <AppEditButton
                            onClick={() => handleEdit(item)}
                            type="button"
                        />
                        <AppRemoveButtonPopup
                            onConfirm={() => handleDelete(item)}
                        />
                    </FlexBox>
                ),
            },
        ],
        [fetchLocations],
    )

    return (
        <>
            <DesktopMode>
                <FlexRowBetween className="mb-3">
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="pointer d-flex align-items-center"
                            onClick={() => navigate('/app/asset-management')}
                        >
                            <CaretLeft size={22} />
                            <span className="ms-1">Back</span>
                        </div>
                        <PageHeader header="Asset Locations" />
                    </div>
                    <div className="d-flex gap-2">
                        <AppSecondaryButton
                            type="button"
                            onClick={() => navigate('/app/asset-management')}
                        >
                            Assets
                        </AppSecondaryButton>
                        <AppPrimaryButton type="button" onClick={handleAdd}>
                            Add Location
                        </AppPrimaryButton>
                    </div>
                </FlexRowBetween>
            </DesktopMode>
            <MobileMode>
                <div className="row mb-3">
                    <div className="col-12">
                        <div
                            className="pointer d-flex align-items-center mb-2"
                            onClick={() => navigate('/app/asset-management')}
                        >
                            <CaretLeft size={22} />
                            <span className="ms-1">Back</span>
                        </div>
                        <PageHeader header="Asset Locations" />
                    </div>
                    <div className="col-12 mt-2">
                        <AppPrimaryButton type="button" onClick={handleAdd} fluid>
                            Add Location
                        </AppPrimaryButton>
                    </div>
                </div>
            </MobileMode>

            <div className="my-3">
                {loading && <LoaderBar content="Loading locations..." />}
                <AppTable
                    data={locationsForUi}
                    columns={columns}
                    totalCount={locationsForUi.length}
                    filter={{ PageNumber: 1, PageSize: 25 }}
                    hidePagination
                    emptyMessage="No locations found"
                />
            </div>

            <Modal open={openModal} onClose={() => setOpenModal(false)} size="small">
                <Modal.Header>
                    <FlexRowBetween>
                        <div style={{ fontWeight: 600, fontSize: '18px' }}>
                            {selectedId ? 'Edit Location' : 'Add Location'}
                        </div>
                        <X
                            className="pointer"
                            size={24}
                            onClick={() => setOpenModal(false)}
                        />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content>
                    <Form>
                        <Form.Input
                            label="Location Name"
                            value={locationForm.LocationName}
                            onChange={(_, { value }) =>
                                setLocationForm(prev => ({
                                    ...prev,
                                    LocationName: value,
                                }))
                            }
                            required
                        />
                        <Form.Input
                            label="Location Code"
                            value={locationForm.LocationCode}
                            onChange={(_, { value }) =>
                                setLocationForm(prev => ({
                                    ...prev,
                                    LocationCode: value,
                                }))
                            }
                        />
                        <Form.Input
                            label="Address 1"
                            value={locationForm.Address1}
                            onChange={(_, { value }) =>
                                setLocationForm(prev => ({
                                    ...prev,
                                    Address1: value,
                                }))
                            }
                        />
                        <Form.Input
                            label="Address 2"
                            value={locationForm.Address2}
                            onChange={(_, { value }) =>
                                setLocationForm(prev => ({
                                    ...prev,
                                    Address2: value,
                                }))
                            }
                        />
                        <Form.Group widths="equal">
                            <Form.Input
                                label="City"
                                value={locationForm.City}
                                onChange={(_, { value }) =>
                                    setLocationForm(prev => ({
                                        ...prev,
                                        City: value,
                                    }))
                                }
                            />
                            <Form.Input
                                label="State"
                                value={locationForm.State}
                                onChange={(_, { value }) =>
                                    setLocationForm(prev => ({
                                        ...prev,
                                        State: value,
                                    }))
                                }
                            />
                            <Form.Input
                                label="Zip"
                                value={locationForm.Zip}
                                onChange={(_, { value }) =>
                                    setLocationForm(prev => ({
                                        ...prev,
                                        Zip: value,
                                    }))
                                }
                            />
                        </Form.Group>
                        <Form.Checkbox
                            label="Active"
                            checked={locationForm.IsActive}
                            onChange={(_, { checked }) =>
                                setLocationForm(prev => ({
                                    ...prev,
                                    IsActive: !!checked,
                                }))
                            }
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <AppSecondaryButton
                        type="button"
                        onClick={() => setOpenModal(false)}
                    >
                        Cancel
                    </AppSecondaryButton>
                    <AppPrimaryButton
                        type="button"
                        onClick={handleSave}
                        loading={saving}
                        disabled={!locationForm.LocationName.trim()}
                    >
                        Save
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
        </>
    )
}
